/* Phase 6 / Polish - <product-card> Custom Element.
 * Behavior:
 *   - Reads variant data from a <script type="application/json"> child.
 *   - On swatch click: rewrites the card's main anchor href to
 *     /products/<handle>?variant=<id>, toggles aria-pressed on
 *     swatch buttons, sets data-selected on the wrapper.
 *   - On quick-add click: prevent navigation, resolve variant_id
 *     (pressed swatch -> dataset.defaultVariantId), call cartStore.add,
 *     dispatch cart:item-added (side cart drawer opens via its listener).
 *   - Desktop hover gallery: mouseenter -> position 1, mouseleave -> 0;
 *     arrow buttons cycle with wraparound (2026-07-11).
 *   - Mobile swipe gallery: same absolute-stacked ladder as desktop; touch
 *     swipe direction increments/decrements data-idx with wraparound.
 * Dispatched events: cart:item-added { item } - on successful quick-add.
 * Consumed events: none.
 */
/* Detect touch vs pointer once, at module scope. Same pattern used to fix
   the PDP hover-preview tap-twice iOS bug in sections/product-buy-box.liquid. */
const supportsHover = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    /* Existing swatches payload (color-mode only). Kept for legacy consumers,
       though the current codebase doesn't read `this.config` anywhere. */
    const configEl = this.querySelector('script[type="application/json"]:not([data-variant-images])');
    this.config = configEl ? JSON.parse(configEl.textContent || '{}') : {};

    /* Variant-image swap payload (2026-07-12). Maps variant.id -> array of
       image objects {src, srcset, w, h, alt}. Emitted by
       snippets/product-card.liquid when card_show_swatches AND
       card_kind == 'product'. Missing for cards without swatches. */
    const variantImagesEl = this.querySelector('script[type="application/json"][data-variant-images]');
    try {
      this._variantImages = variantImagesEl ? JSON.parse(variantImagesEl.textContent || '{}') : {};
    } catch (err) {
      console.error('[product-card] variant-images payload malformed', err);
      this._variantImages = {};
    }

    this.anchors = this.querySelectorAll('a[data-product-link]');
    this.handle = this.dataset.handle || '';
    this.defaultVariantId = this.dataset.defaultVariantId || '';
    this.addEventListener('click', this._onClick);

    /* --- Hover gallery (2026-07-11) --- */
    this._media = this.querySelector('.product-card__media');
    this._imageCount = parseInt(this._media?.dataset.imageCount, 10) || 1;
    /* Bind hover / swipe unconditionally so a later swatch swap that
       changes the image count (e.g. initial variant has 1 image, swapped
       variant has 5) still cycles correctly. Handlers guard on
       `this._imageCount >= 2` at gesture time. */
    if (this._media) {
      if (supportsHover) this._bindHover();
      else this._bindMobileSwipe();
    }
  }

  /* Desktop only: card mouseenter jumps to gallery position 1; mouseleave
     returns to position 0. Arrow buttons cycle with wraparound. Arrows
     preventDefault + stopPropagation on click so their click doesn't
     navigate the card's parent <a>. They ALSO preventDefault on mousedown
     to suppress the browser's default "start text selection" behavior -
     without it, Chrome/Safari can interpret the mousedown-inside-anchor
     as the start of a selection range that extends to the title text
     below on release, briefly highlighting the title (reported 2026-07-12). */
  _bindHover() {
    this.addEventListener('mouseenter', () => {
      if (this._imageCount >= 2) this._setIdx(1);
    });
    this.addEventListener('mouseleave', () => this._setIdx(0));
    this._media.querySelectorAll('[data-nav]').forEach((btn) => {
      btn.addEventListener('mousedown', (e) => e.preventDefault());
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this._imageCount < 2) return;
        const dir = parseInt(btn.dataset.nav, 10);
        const curr = parseInt(this._media.dataset.idx, 10) || 0;
        this._setIdx((curr + dir + this._imageCount) % this._imageCount);
      });
    });
  }

  /* Mobile only: mirror desktop's arrow-cycle behavior. Same absolute-stacked
     ladder + `_setIdx`; the only difference is the trigger — touch swipe
     instead of arrow click. Wraparound identical: `(curr + dir + N) % N`.
     Leftward finger drag = next image; rightward = previous.

     Axis lock: on the first ~10px of movement, decide horizontal vs vertical
     from `abs(dx)` vs `abs(dy)`. If horizontal, preventDefault every
     touchmove for the rest of the gesture so the page doesn't scroll
     vertically at the same time (reported 2026-07-11). If vertical, we
     leave scroll alone and skip image cycling on touchend. */
  _bindMobileSwipe() {
    const gallery = this._media.querySelector('.product-card__gallery');
    if (!gallery) return;
    const THRESHOLD = 40;
    const AXIS_LOCK = 10;
    let startX = 0;
    let startY = 0;
    let tracking = false;
    let axis = null;

    gallery.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      tracking = true;
      axis = null;
    }, { passive: true });

    gallery.addEventListener('touchmove', (e) => {
      if (!tracking) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (axis === null) {
        if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (axis === 'x') e.preventDefault();
    }, { passive: false });

    gallery.addEventListener('touchend', (e) => {
      if (!tracking) return;
      tracking = false;
      if (axis !== 'x') return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) < THRESHOLD) return;
      /* Suppress the ancestor <a>'s tap-navigate for this gesture only;
         a real tap (dx below threshold) still navigates as normal. */
      e.preventDefault();
      /* Read image count at gesture-time (not bind-time). A swatch swap
         via _swapGalleryTo may have changed the gallery's layer count
         since the CE was connected; closured N would use the stale count
         and cycle past visible layers into blank positions (bug reported
         2026-07-12: 3-image gallery cycled through 5 positions leaving
         2 blank slides). */
      const N = this._imageCount;
      if (N < 2) return;
      const dir = dx < 0 ? 1 : -1;
      const curr = parseInt(this._media.dataset.idx, 10) || 0;
      this._setIdx((curr + dir + N) % N);
    });
  }

  /* Update the visible layer via data-idx (CSS attribute selector reacts).
     Used by both desktop and mobile. */
  _setIdx(next) {
    if (!this._media) return;
    this._media.dataset.idx = String(next);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick(event) {
    const quickAdd = event.target.closest('[data-quick-add]');
    if (quickAdd) {
      event.preventDefault();
      this._handleQuickAdd(quickAdd);
      return;
    }

    const swatch = event.target.closest('.product-card__swatch');
    if (!swatch) return;
    event.preventDefault();
    const variantId = swatch.dataset.variantId;
    if (!variantId) return;

    this.querySelectorAll('.product-card__swatch').forEach((b) => {
      b.setAttribute('aria-pressed', b === swatch ? 'true' : 'false');
    });

    /* Per-swatch sold-out reveal (2026-07-13). Treat any value other than
       the literal "false" as available so a missing attribute (older
       renders / non-swatched consumers) doesn't force sold-out. */
    this.dataset.soldOut = swatch.dataset.variantAvailable === 'false' ? 'true' : 'false';

    if (this.handle && this.anchors.length) {
      const newHref = `/products/${this.handle}?variant=${variantId}`;
      this.anchors.forEach((a) => { a.href = newHref; });
    }

    this._swapGalleryTo(variantId);
  }

  /* Swap gallery layer <img>s to the clicked variant's resolved image set.
     No-ops when the payload doesn't include this variant (e.g. products
     with neither variant image sets nor variant.image - swatch click still
     rewrites the URL, but there's nothing to swap the image to).

     Existing layers are updated in place; layers beyond the new image
     count are hidden; if the new set has MORE images than existing layers,
     new layers are created and appended to the gallery. Layer count also
     updates data-image-count on .product-card__media so the hover /
     mobile-swipe cycling honors the new bounds.

     After swap, data-idx resets to 0 so the hero (position 0) is visible. */
  _swapGalleryTo(variantId) {
    if (!this._variantImages) return;
    const images = this._variantImages[variantId];
    if (!images || !images.length) return;
    const gallery = this._media && this._media.querySelector('.product-card__gallery');
    if (!gallery) return;

    let layers = Array.from(gallery.querySelectorAll('.product-card__image'));
    const newCount = Math.min(images.length, 5);

    /* Populate up to newCount layers, creating missing ones. */
    for (let i = 0; i < newCount; i++) {
      let layer = layers[i];
      if (!layer) {
        layer = document.createElement('div');
        layer.className = 'product-card__image' + (i === 0 ? ' product-card__image--hero' : '');
        layer.setAttribute('data-position', String(i));
        const img = document.createElement('img');
        img.loading = 'lazy';
        img.decoding = 'async';
        layer.appendChild(img);
        gallery.appendChild(layer);
        layers.push(layer);
      } else {
        layer.hidden = false;
      }
      const img = layer.querySelector('img');
      if (!img) continue;
      const data = images[i];
      img.src = data.src;
      if (data.srcset) img.srcset = data.srcset;
      if (data.w) img.width = data.w;
      if (data.h) img.height = data.h;
      img.alt = data.alt || '';
    }

    /* Hide any leftover layers. */
    for (let i = newCount; i < layers.length; i++) {
      layers[i].hidden = true;
    }

    /* Update image count + reset visible layer. */
    this._imageCount = newCount;
    if (this._media) {
      this._media.dataset.imageCount = String(newCount);
    }
    this._setIdx(0);
  }

  _resolveVariantId() {
    const pressed = this.querySelector('.product-card__swatch[aria-pressed="true"]');
    if (pressed && pressed.dataset.variantId) return pressed.dataset.variantId;
    return this.defaultVariantId || '';
  }

  async _handleQuickAdd(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    if (!window.cartStore || typeof window.cartStore.add !== 'function') {
      console.error('[product-card] cartStore unavailable for quick-add');
      return;
    }
    const variantId = this._resolveVariantId();
    if (!variantId) {
      console.error('[product-card] no variant id for quick-add', this.handle);
      return;
    }

    button.setAttribute('aria-busy', 'true');
    try {
      const item = await window.cartStore.add({ id: variantId, quantity: 1 });
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { item },
        bubbles: false
      }));
    } catch (err) {
      console.error('[product-card]', 'quick-add-failed', err);
    } finally {
      button.setAttribute('aria-busy', 'false');
    }
  }
}

customElements.define('product-card', ProductCard);
